import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Modal, Row, Col, Timeline } from "antd";
import styles from "./DashBoardMobile.module.scss";
import commonStyles from "./../DashBoard.module.scss";
import DashBoardGmaps from "../DashBoardGmaps";
import moment from "moment";
import getTripIcon from "../../../helpers/getTripIcon";
import {
  backArrowIcon,
  pickupTimelineIcon,
  homeIcon,
  dropoffTimelineIcon,
} from "../../../assets/images";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import CustomButton from "../../../common/Buttons/CustomButton";
import TripActionButtons from "../../Shared/TripActionButtons";

type Props = {
  selectedLeg: ITripDetails;
  resetToggleDetailsView: Function;
};

const DashBoardMobileServiceDetails: React.FC<Props> = ({ selectedLeg, resetToggleDetailsView }) => {
  const [isMapModalOpen, setIsMapModalOpen] = useState<boolean>(false);

  const history = useHistory();

  const handleBack = (): void => resetToggleDetailsView();

  const handleBookingId = (record: ITripDetails) => {
    history.push(
      `/DashBoard/TransportationServiceSetupSummary/${record.personId}/${record.requestAssignmentId}/${record.bookingId}/${record.tripStatusId}/${record.tripDate}`
    );
  };

  const serviceDetailRow = (
    leftHeading: string,
    leftContent: string,
    rightHeading: string,
    rightContent: string
  ): JSX.Element => {
    return (
      <Row className={styles.serviceDetailsRows}>
        <Col span={12}>
          <Row>
            <Col span={24} className={styles.detailsHeading}>
              {leftHeading}
            </Col>
            <Col span={24} className={styles.detailsInfo}>
              {leftContent}
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row>
            <Col span={24} className={styles.detailsHeading}>
              {rightHeading}
            </Col>
            <Col span={24} className={styles.detailsInfo}>
              {rightContent}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Button type="link" className={styles.backBtn} onClick={handleBack}>
            <img src={backArrowIcon} alt="back button" />
            Back
          </Button>
        </Col>
      </Row>
      <Row className={styles.serviceTitleMargin}>
        <Col span={24}>
          <p className={styles.title}>Service Details</p>
        </Col>
      </Row>

      {serviceDetailRow(
        "Date",
        moment(selectedLeg?.tripDate).format("MM/DD/YY"),
        "Time",
        selectedLeg.isWillCall
          ? "Call when ready"
          : moment(
              selectedLeg.appointmentTime || selectedLeg.requestPickTime
            ).format("hh:mm A")
      )}

      <Row className={styles.serviceDetailsRows}>
        <Col span={12}>
          <Row>
            <Col span={24} className={styles.detailsHeading}>
              Service Type
            </Col>
            <Col span={24} className={styles.serviceTypeDetailsAlign}>
              <span className={styles.tripImageSmall}>
                {getTripIcon(selectedLeg?.tripReason)}
              </span>
              <span className={styles.detailsInfo}>Ride</span>
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row>
            <Col span={24} className={styles.detailsHeading}>
              Service User
            </Col>
            <Col span={24} className={styles.detailsInfo}>
              {selectedLeg?.serviceUser}
            </Col>
          </Row>
        </Col>
      </Row>

      {serviceDetailRow(
        "Cost",
        `$${selectedLeg?.tripCost}`,
        "Status",
        selectedLeg?.statusDetail
      )}

      <div className={styles.mobileMapsContainer}>
        <Row align="middle" justify="center">
          <Col
            xs={{ span: 4, offset: 2 }}
            sm={{ span: 4, offset: 4 }}
            md={{ span: 4, offset: 6 }}
            className={commonStyles.distanceText}
          >
            <Row
              justify="end"
              align="middle"
              className={commonStyles.distanceMargin}
            >
              <Col>{selectedLeg?.providerDistance} mi</Col>
            </Row>
          </Col>
          <Col xs={18} sm={16} md={14} className={commonStyles.customTimeline}>
            <Timeline>
              <Timeline.Item dot={<img src={pickupTimelineIcon} />}>
                <Row>
                  <Col span={24} className={commonStyles.addressHeading}>
                    Pickup Address
                  </Col>
                  <Col span={24} className={commonStyles.addressDetails1}>
                    <img src={homeIcon} alt="pickup address" />
                    <span>{selectedLeg?.pickUpAddress1}</span>
                  </Col>
                  <Col span={24} className={commonStyles.addressDetails2}>
                    {selectedLeg?.pickUpCity}, {selectedLeg?.pickUpState}{" "}
                    {selectedLeg?.pickUpZipCode.toString().padStart(5, "0")}
                  </Col>
                </Row>
              </Timeline.Item>
              <Timeline.Item dot={<img src={dropoffTimelineIcon} />}>
                <Row>
                  <Col span={24} className={commonStyles.addressHeading}>
                    Dropoff Address
                  </Col>
                  <Col span={24} className={commonStyles.addressDetails1}>
                    {selectedLeg?.dropOffAddress1}
                  </Col>
                  <Col span={24} className={commonStyles.addressDetails2}>
                    {selectedLeg?.dropOffCity}, {selectedLeg?.dropOffState}{" "}
                    {selectedLeg?.dropOffZipCode.toString().padStart(5, "0")}
                  </Col>
                </Row>
              </Timeline.Item>
            </Timeline>
          </Col>
        </Row>

        <DashBoardGmaps
          setIsMapModalOpen={setIsMapModalOpen}
          tripDetails={selectedLeg}
          width={"auto"}
          height="167px"
          isCalling="mobile"
        />
      </div>

      {(selectedLeg?.pickupNotes !== "" || selectedLeg?.dropNotes !== "") &&
        serviceDetailRow(
          "Pickup Instructions",
          selectedLeg?.pickupNotes,
          "Dropoff Instructions",
          selectedLeg?.dropNotes
        )}

      <Row className={styles.serviceDetailsRows}>
        <Col span={12}>
          <Row>
            <Col span={24} className={styles.detailsHeading}>
              Trip ID
            </Col>
            <Col span={24} className={styles.tripId}>
              {selectedLeg?.tripId}
            </Col>
          </Row>
        </Col>
        <Col span={12}>
          <Row justify="center">
            <Col span={24} className={styles.detailsHeading}>
              Booking ID
            </Col>
            <Col className={styles.detailsInfo}>
              <CustomButton
                text={selectedLeg?.bookingId}
                type="link"
                handlerFunction={() => handleBookingId(selectedLeg)}
                className="hyperLinkBtn"
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <TripActionButtons
        allTripLegs={[]}
        selectedLeg={selectedLeg}
      />

      <Modal
        visible={isMapModalOpen}
        footer={null}
        className={styles.expandedMap}
      >
        <DashBoardGmaps
          isMapModalOpen={isMapModalOpen}
          setIsMapModalOpen={setIsMapModalOpen}
          tripDetails={selectedLeg}
          width={"auto"}
          height={"458px"}
          isCalling="mobile"
        />
      </Modal>
    </>
  );
};
export default DashBoardMobileServiceDetails;
